import { DateTime } from 'luxon';
import { darken, readableColor, rem } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { camelize } from '@vimeo/core/libs/camelize';
import { translate } from '@vimeo/i18n-runtime';
import { red } from '@vimeo/iris/color';
import { Button } from '@vimeo/iris/components';
import { LoaderCircular } from '@vimeo/iris/motion';
import { Header } from '@vimeo/iris/typography';

import { ClipDataObject, LiveStatus, RecurringEvent } from '../../types';
import { VIDEO_KEY } from '../../utils/constants';
import { idFromUri, trackPotentialError, truncate } from '../../utils/helpers';
import LiveBadge from '../LiveBadge';
import LiveSchedule from '../LiveSchedule';

type Props = {
  albumId: number;
  brandColor: string;
  onPlay: VoidFunction;
  featuredVideo: ClipDataObject;
  logo: boolean;
  name: string;
  defaultThumbnail?: string;
  apiUrl: string;
  apiJwt: string;
  hashedPass?: string;
};

const TRANSITION_DURATION = 2000;
const LONG_POLL_DELAY = 15000;
const MEDIUM_POLL_DELAY = 8000;
const SHORT_POLL_DELAY = 5000;
const MAX_TITLE_LENGTH = 60;

const getPollDelay = (recurringEvent: RecurringEvent, status: LiveStatus) => {
  const { schedule } = recurringEvent;
  if (schedule) {
    const scheduleDate = schedule.startTime;
    const nowDateTime = DateTime.fromJSDate(new Date());
    const startDateTime = DateTime.fromISO(scheduleDate);
    const diff = startDateTime.diff(nowDateTime, ['hours']);

    if (status === 'streaming') {
      return MEDIUM_POLL_DELAY;
    }

    if (diff.hours > 24) {
      return LONG_POLL_DELAY;
    } else if (diff.hours > 12) {
      return MEDIUM_POLL_DELAY;
    }
  }
  // Ad-hoc and 12 hours or less away from start time get short delay
  return SHORT_POLL_DELAY;
};

export default function LiveHero({
  albumId,
  brandColor,
  onPlay,
  featuredVideo,
  logo,
  name,
  defaultThumbnail,
  apiUrl,
  apiJwt,
  hashedPass,
}: Props) {
  const [eventIsStarting, setEventIsStarting] = useState(false);
  const [liveStatus, setLiveStatus] = useState(featuredVideo.live && featuredVideo.live.status);
  const [schedule, setSchedule] = useState(
    featuredVideo.live &&
      featuredVideo.live.recurringEvent &&
      featuredVideo.live.recurringEvent.schedule,
  );
  const [pollId, settPollId] = useState<number | null>(null);

  useEffect(() => {
    if (['unavailable', 'pending', 'ready', 'streaming'].some((status) => status === liveStatus)) {
      startPolling();
    }
    return () => {
      if (pollId) {
        clearInterval(pollId);
      }
    };
  }, [liveStatus]);

  const onLiveEventPlay = () => {
    setEventIsStarting(true);

    setTimeout(() => {
      onPlay();
      setEventIsStarting(false);
    }, TRANSITION_DURATION);
  };

  const startPolling = () => {
    const { live } = featuredVideo;
    if (live && live.recurringEvent) {
      const delay = getPollDelay(live.recurringEvent, live.status);
      const pollId = setInterval(fetchLiveStatus, delay);

      settPollId(pollId);
    }
  };

  const fetchLiveStatus = () => {
    fetch(
      `https://${apiUrl}/albums/${albumId}/videos/${idFromUri(
        featuredVideo?.uri,
        VIDEO_KEY,
      )}?_hashed_pass=${hashedPass}&fields=live.status,live.recurring_event.schedule`,
      {
        headers: {
          Accept: 'application/vnd.vimeo.*+json;version=3.4.9',
          Authorization: `jwt ${apiJwt}`,
          'Content-Type': 'application/json',
        },
      },
    )
      .then((res) => {
        trackPotentialError(res, 'fetchLiveStatus');
        // @ts-ignore errorCode not standard in fetch Response type
        if (!res.ok && res.errorCode === 8003) {
          // If the JWT token expired refresh the page
          return location.reload();
        }
        return res.json();
      })
      .then((res) => {
        if (res.live && res.live.status) {
          if (res.live.status === 'done' && liveStatus === 'streaming') {
            return location.reload();
          }
          const updatedSchedule = res.live.recurring_event && res.live.recurring_event.schedule;
          setLiveStatus(res.live.status);
          if (updatedSchedule) {
            setSchedule(camelize(updatedSchedule));
          }
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((error) => {
        return location.reload();
      });
  };

  const isArchivedOrNonLive =
    !featuredVideo.live || (liveStatus === 'done' && featuredVideo.live.archivedTime);

  let heroThumbnailUrl = defaultThumbnail; // Default to showcase's thumbnail

  // Use the featured video's thumbnail if it's a VOD/archived live clip.
  if (isArchivedOrNonLive && featuredVideo.pictures) {
    const thumbnail = featuredVideo.pictures.sizes[featuredVideo.pictures.sizes.length - 1].link;

    const thumbnailURL = thumbnail.substring(0, thumbnail.lastIndexOf('_'));
    heroThumbnailUrl = thumbnail && thumbnail ? `${thumbnailURL}_1680x945` : '';
  }

  const isStreaming = liveStatus === 'streaming';
  const watchButtonText = isStreaming ? translate('Watch now') : translate('Watch');

  return (
    <Hero thumbnail={heroThumbnailUrl}>
      {eventIsStarting ? (
        <EventIsStarting>
          <BigRedLoader />
          <LoaderText>{translate('Event is starting')}</LoaderText>
        </EventIsStarting>
      ) : (
        <Details>
          <Metadata>
            {logo && <ShowcaseName>{name}</ShowcaseName>}
            {featuredVideo.live && !featuredVideo.live.archivedTime && (
              <LiveBadge status={liveStatus} />
            )}
            <Title title={featuredVideo.name}>
              {featuredVideo.name && truncate(featuredVideo.name, MAX_TITLE_LENGTH)}
            </Title>
            {featuredVideo.description && <Description>{featuredVideo.description}</Description>}
          </Metadata>
          {/* Do not show WatchButton for pre-live clips */}
          {(isStreaming || isArchivedOrNonLive) && (
            <WatchButton
              brandColor={brandColor}
              onClick={() => {
                isStreaming ? onLiveEventPlay() : onPlay();
                FA.trackClick({
                  container: 'showcase',
                  component: isStreaming ? 'live_event' : 'video',
                  keyword: 'watch',
                });
              }}
            >
              {watchButtonText}
            </WatchButton>
          )}
          {schedule && !isArchivedOrNonLive && !isStreaming && <LiveSchedule schedule={schedule} />}
        </Details>
      )}
    </Hero>
  );
}

type HeroProps = {
  thumbnail?: string;
};

const Hero = styled.div<HeroProps>`
  width: 100%;
  height: 80vh;
  min-height: ${rem(600)};
  background-color: black;
  background-position: center;
  background-size: cover;
  background-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.95) 0%,
      rgba(0, 0, 0, 0.85) 35%,
      rgba(0, 0, 0, 0.25) 100%
    ),
    url(${(props) => props.thumbnail});

  @media only screen and (max-width: 767px) {
    min-height: ${rem(480)};
    height: ${rem(480)};
  }
`;

interface ButtonProps {
  brandColor: string;
}

function getColorStyle(props: ButtonProps, isDarkened: boolean = false) {
  let color = props.brandColor ? props.brandColor : red(500);
  if (isDarkened) {
    color = props.brandColor ? darken(0.15, props.brandColor) : red(600);
  }
  return `background-color: ${color};
          border-color: ${color};
          color: ${readableColor(color)}`;
}

const WatchButton = styled(Button)<{ brandColor: string }>`
  ${getColorStyle};
  min-width: ${rem(194)};
  width: auto;
  margin-top: ${rem(15)};
  text-align: center;
  height: ${rem(50)};

  &:hover {
    ${(props) => getColorStyle(props, true)};
  }
`;

const Details = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const Title = styled(Header).attrs({ size: 'plusUltra' })`
  color: white;
  text-align: center;
  overflow: hidden;
  font-size: ${rem(72)};
  line-height: ${rem(84)};
  letter-spacing: ${rem(-2)};

  @media only screen and (max-width: 1024px) {
    font-size: ${rem(55)};
    line-height: ${rem(64)};
    letter-spacing: ${rem(-1.5)};
  }

  @media only screen and (max-width: 767px) {
    font-size: ${rem(40)};
    line-height: ${rem(46)};
    letter-spacing: ${rem(-1)};
    padding: 0 ${rem(10)};
  }
`;

const Description = styled(Header).attrs({ size: '5' })`
  color: #8498a4;
  text-align: center;
  display: -webkit-box;
  margin-bottom: ${rem(30)};
  font-weight: normal;
  line-height: ${rem(22)};
  max-height: ${rem(44)};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media only screen and (max-width: 767px) {
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    max-width: 80%;
    margin: 0 auto ${rem(10)};
    max-height: ${rem(36)};
  }
`;

const Metadata = styled.div`
  margin: 0 auto;
  padding: 0 ${rem(15)};
  text-align: center;
`;

const BigRedLoader = styled(LoaderCircular)`
  height: ${rem(96)};
  width: ${rem(96)};
  border-color: ${red(500)};
  border-radius: 50%;
`;

const LoaderText = styled(Header).attrs({ size: '2' })`
  color: white;
  margin-top: ${rem(25)};
`;

const EventIsStarting = styled.div`
  height: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const ShowcaseName = styled(Header).attrs({ size: '5' })`
  color: white;
  margin-bottom: ${rem(30)};
  display: none;

  @media only screen and (max-width: 767px) {
    display: block;
  }
`;
